"use client";

/**
 * The History & Audit FEED — the one implementation behind both entry points.
 *
 * Plan: apps/hub/docs/development/entity-audit-log-s3-plan.md → "UI
 * specification", whose last line is the requirement this file exists to keep
 * honest: *"The same components serve the P4 detail-modal History tab — one
 * implementation, two entry points."*
 *
 * ## Why this is separate from `EntityAuditModal`
 *
 * Everything a reader of history needs — the paged feed, per-event expansion,
 * the two-step restore and its confirmation — is the same on both surfaces.
 * What differs is only the CHROME: the modal is a browser-history-backed PLACE
 * (`MODAL_URL_CONFIG`, `router.back()` on close, a `ResponsiveModal` shell),
 * while the detail-modal History tab is a panel inside a modal that already
 * owns its own history entry.
 *
 * So this component deliberately makes NO modal assumptions: it takes an
 * `entityId` and renders. `EntityAuditModal` wraps it in the place-modal shell;
 * `<Entity>DetailTabComponents` renders it directly. Forking a second feed for
 * the tab is the thing this file prevents — a divergence in an AUDIT surface
 * means two different answers to "what happened to this record".
 *
 * The ONE piece of modal machinery that stays is `RestoreConfirmDialog`: the
 * confirmation is a global-stack modal by design (a *moment*, deliberately not
 * in `MODAL_URL_CONFIG` — Forward could not faithfully restore a half-confirmed
 * write). It is rendered only while THIS instance owns a pending restore,
 * because the stack id is shared across every mount.
 *
 * ## What lives next door
 *
 * This file is the composition. The restore state machine is
 * `EntityAuditFeed/useAuditRestoreFlow`, the toast wording is
 * `EntityAuditFeed/restore-summary`, and the not-a-feed screens are
 * `EntityAuditFeed/AuditFeedStates`.
 */

import React, { useCallback, useMemo, useState } from "react";
import { MODAL_IDS, useAuth, useGlobalModalStack } from "@west-tennessee-consulting/ui";

import type { AuditEventDto, AuditableEntityType } from "@/lib/audit/api-contract";
import { useEntityAuditHistory } from "@/lib/hooks/api/useEntityAuditHistory";
import { cn } from "@/lib/utils";

import { AuditEventRow, formatAuditEventTime } from "./AuditEventRow";
import { RestoreConfirmDialog } from "./RestoreConfirmDialog";
import {
  AuditFeedEmptyState,
  AuditFeedErrorState,
  AuditFeedFooter,
  AuditFeedNoRecordState,
} from "./EntityAuditFeed/AuditFeedStates";
import { errorMessageOf } from "./EntityAuditFeed/restore-summary";
import { useAuditRestoreFlow } from "./EntityAuditFeed/useAuditRestoreFlow";

export interface EntityAuditFeedProps {
  entityType: AuditableEntityType;
  /**
   * The record whose history this is, or `null` when the surface has no record
   * yet (a cold deep link that carried no id, or a detail tab rendered for an
   * item whose id was not selected in the query).
   *
   * NULL IS NOT "SHOW EVERYTHING". A feed keyed on a missing id must render an
   * explanatory empty state, never an unscoped read — the same failure mode
   * DETAIL_MODAL_TAB_COVERAGE guards for nested tables, where a dropped filter
   * silently widens a per-record panel into the whole tenant. Here the guard is
   * an early return below AND `enabled: Boolean(entityId)` in the read hook, so
   * no request is even issued.
   */
  entityId: string | null;
  /** Whose history — named in the confirmation so it cannot be misread. */
  subjectLabel: string;
  /**
   * False while the surface is not being looked at (a closed modal). The read is
   * one S3 GET, but it is still a read of customer history: do not issue it for
   * a surface nobody opened.
   */
  active?: boolean;
  /** Tailwind max-height for the scroller. The modal caps it; a tab does not. */
  scrollAreaClassName?: string;
  /** Copy for the no-record case, so each entry point can be specific. */
  emptyParentTitle?: string;
  emptyParentDescription?: string;
  className?: string;
}

export function EntityAuditFeed({
  entityType,
  entityId,
  subjectLabel,
  active = true,
  scrollAreaClassName,
  emptyParentTitle = "No record selected",
  emptyParentDescription = "Change history is recorded per record, so there is nothing to show without one.",
  className,
}: EntityAuditFeedProps) {
  const { isAdmin } = useAuth();
  const { openModal: openStackModal, closeModal: closeStackModal } = useGlobalModalStack();

  // --- feed ----------------------------------------------------------------
  const history = useEntityAuditHistory({
    entityType,
    entityId,
    enabled: active,
  });

  const events = useMemo<AuditEventDto[]>(
    () => (history.data?.pages ?? []).flatMap((page) => page.events),
    [history.data]
  );
  const readSealedMonths = useMemo(
    () => (history.data?.pages ?? []).some((page) => page.readSealedMonths),
    [history.data]
  );

  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const toggleExpanded = useCallback((eventId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  // --- restore (two-step) --------------------------------------------------
  const openConfirm = useCallback(
    () => openStackModal(MODAL_IDS.audit.restoreConfirm),
    [openStackModal]
  );
  const closeConfirm = useCallback(
    () => closeStackModal(MODAL_IDS.audit.restoreConfirm),
    [closeStackModal]
  );

  const restore = useAuditRestoreFlow({
    entityType,
    entityId,
    active,
    openConfirm,
    closeConfirm,
  });

  // `fetchNextPage` / `refetch` are stable across renders; the query object is
  // not, so depend on the functions rather than on `history`.
  const { fetchNextPage, refetch } = history;
  const handleLoadOlder = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  // EARLY RETURN, not a conditional branch further down: nothing below may run a
  // read or offer a restore control for a record that was never identified.
  if (!entityId) {
    return (
      <AuditFeedNoRecordState
        title={emptyParentTitle}
        description={emptyParentDescription}
        className={className}
      />
    );
  }

  const showLoading = history.isLoading && events.length === 0;

  return (
    <div className={cn("flex flex-col overflow-hidden", className)}>
      <div className={cn("min-h-0 flex-1 overflow-y-auto", scrollAreaClassName)}>
        {showLoading && <p className="p-4 text-sm text-muted-foreground">Loading history…</p>}

        {history.isError && events.length === 0 && (
          <AuditFeedErrorState message={errorMessageOf(history.error)} onRetry={handleRetry} />
        )}

        {!showLoading && !history.isError && events.length === 0 && <AuditFeedEmptyState />}

        {events.length > 0 && (
          <ul data-testid="audit-event-list" className="divide-y divide-border">
            {events.map((event) => (
              <AuditEventRow
                key={event.eventId}
                event={event}
                expanded={expandedIds.has(event.eventId)}
                onToggle={toggleExpanded}
                onRestore={restore.requestRestore}
                restoreDisabled={restore.isBusy}
              />
            ))}
          </ul>
        )}
      </div>

      <AuditFeedFooter
        readSealedMonths={readSealedMonths}
        hasNextPage={Boolean(history.hasNextPage)}
        isFetchingNextPage={history.isFetchingNextPage}
        onLoadOlder={handleLoadOlder}
      />

      {/* Rendered only while THIS instance owns a pending restore: the confirm
          dialog's stack id is shared, so an unconditional render in every mount
          (two history modals plus the detail-modal History tab) would open
          several dialogs at once. */}
      {restore.pendingEvent && (
        <RestoreConfirmDialog
          subjectLabel={subjectLabel}
          eventTimeLabel={formatAuditEventTime(restore.pendingEvent.at)}
          plan={restore.plan}
          isPlanning={restore.isPlanning}
          isApplying={restore.isApplying}
          planChanged={restore.planChanged}
          errorMessage={restore.errorMessage}
          adminRequiredButMissing={Boolean(restore.plan?.requiresAdmin) && !isAdmin}
          onCancel={restore.dismissRestore}
          onConfirm={restore.confirmRestore}
        />
      )}
    </div>
  );
}
